<template>
  <div class="gp-main-body-content">
    <div class="row">
      <div class="col-6 col-mlg-5">
        <FilterSelect v-model="templateType" bpadding="px-1 ml-n1" @input="updateRoute" :items="typeList">
          <template #current="{ active }">
            <h1 class="typo-heading24 typo-semibold text-left template-for">{{ $t("page.templateFor", { type: active }) }}</h1>
            <i class="icon icon-arrow-down ml-half typo-button14 typo-medium"></i>
          </template>
        </FilterSelect>
      </div>
      <div class="col-12">
        <p class="typo-button14 typo-medium text-light-low">{{ currentDescription }}</p>
      </div>
    </div>
    <div class="row mt-3 mb-1">
      <div class="col-12">
        <hr class="bg-dark-high" />
      </div>
    </div>
    <div class="row">
      <AddPage @onClickAddPage="openBlankModal" />
      <template v-if="!fetched">
        <PageTemplateItem v-for="i in 19" :key="'ap_' + i" />
      </template>
      <template v-if="fetched">
        <PageTemplateItem :item="template" v-for="(template, i) in templates" :primaryAction="enterName" :secondaryAction="previewTemplate" :shopIsPro="shopIsPro" :key="i" />
      </template>
    </div>

    <!-- Modal Preview Template -->
    <PreviewTemplate v-if="showPreview" :template="template_id" @close="closePreview" @select="selectTemplate" :list="templates" :hideEdit="accountExpired" />

    <!-- Modal Create Page -->
    <ModalCreatePage :defaultName="defaultPageName" v-if="showModal" :shop="shopInfo.shopifyDomain" :template.sync="template_id" :type.sync="type" @close="closeModal" />
  </div>
</template>

<script>
import { PreviewTemplate, PageTemplateItem, ModalCreatePage } from "../components";
import AddPage from "@/core/shared/GpAddPage";
import { mapGetters, mapActions, mapState } from "vuex";
import FilterSelect from "@/modules/Dashboard/components/FilterSelect.vue";
import { readI18n } from "@/i18n.js";
import { LANDING_PAGE_EVENT, HOME_PAGE_EVENT, PRODUCT_PAGE_EVENT, COLLECTION_PAGE_EVENT, BLOG_POST_EVENT } from "@/config/constants/trackEventIds";
import newObsLocalData from "@/utils/newObsLocalData.js";

export default {
  name: "PageTemplate",
  components: {
    PageTemplateItem,
    AddPage,
    PreviewTemplate,
    ModalCreatePage,
    FilterSelect
  },
  props: {
    item: {
      type: Object,
      default: () => {
        return {};
      }
    },
    type: String
  },
  data() {
    return {
      showPreview: false,
      showModal: false,
      template_id: "0",
      fetched: false,
      templateType: "subpages",
      defaultPageName: "",
      templateOrder: ["landingpages", "subpages", "", "homepages", "productpages", "collectionpages", "articlepages"]
    };
  },
  computed: {
    ...mapGetters("page", {
      allTemplates: "allTemplates",
      getTemplate: "getTemplate"
    }),
    ...mapGetters("account", {
      shopIsPro: "shopIsPro"
    }),
    ...mapState("account", {
      shopInfo: "shopInfo",
      permissions: "permissions"
    }),
    accountExpired() {
      return this.shopInfo.plan == "trial" && this.shopInfo.remainingDays <= 0;
    },
    selectedTemplate() {
      let output = this.getTemplate(this.template_id);
      return output;
    },
    templates() {
      let arr = JSON.parse(JSON.stringify(this.allTemplates));

      arr.forEach(item => {
        if (this.templateOrder.includes(item.tags)) return;
        let listTags = item.tags.split(",");
        listTags.forEach(tag => {
          if (this.templateOrder.includes(tag)) {
            if (!item.tags.endsWith("pages") && tag.endsWith("pages")) return;
            item.tags = tag;
          }
        });
      });

      const orderByType = this.templateOrder.reduce((obj, key, idx) => Object.assign(obj, { [key]: idx + 1 }), {});
      const getVal = item => orderByType[item.tags];

      const sortCondition = (a, b) => {
        if (getVal(a) < getVal(b)) return -1;
        if (getVal(a) > getVal(b)) return 1;
        if (getVal(a) == getVal(b)) {
          if (a.status == "new" && b.status != "new") return -1;
          if (a.status == b.status) {
            if (a.view > b.view) return -1;
          }
        }
      };

      arr.sort(sortCondition);
      return arr;
    },
    currentDescription() {
      return readI18n(this, this.$route.meta.headerDescription);
    },
    typeList() {
      return [
        {
          value: "LandingPagesTemplate",
          text: this.$t("page.landingPages2")
        },
        {
          value: "HomePagesTemplate",
          text: this.$tc("page.homepages", 2)
        },
        {
          value: "ProductPagesTemplate",
          text: this.$tc("page.productpages", 2)
        },
        {
          value: "CollectionPagesTemplate",
          text: this.$tc("page.collectionpages", 2)
        },
        {
          value: "ArticlePagesTemplate",
          text: this.$tc("page.blog", 2)
        }
      ];
    }
  },
  watch: {
    type(newV, oldV) {
      if (newV == oldV) return;
      this.setTemplateType(newV);
    }
  },
  created() {
    this.trackTemplateViewedEvent();
    this.setTemplateType(this.type);
    this.fetch();
    const q = this.$route.query;
    if ((q && q.create == "true") || q.create == true) {
      this.defaultPageName = q["template-name"] ? q["template-name"] : "";
      this.openBlankModal();
    }
  },
  mounted() {
    const shopifyDomain = this.shopInfo.shopifyDomain;
    if (newObsLocalData.check(shopifyDomain)) {
      const lastSurvey = newObsLocalData.get(shopifyDomain);
      if (lastSurvey && lastSurvey.store && lastSurvey.store.industries) {
        const userIndustries = lastSurvey.store.industries;
        userIndustries.reverse().forEach(item =>
          this.templateOrder.unshift(
            item
              .toLowerCase()
              .replace(" & ", "-")
              .replace(", ", "-")
              .replace(" ", "-")
          )
        );
      }
    } else {
      this.getUserIndustryServer();
    }
  },
  methods: {
    ...mapActions("page", {
      fetchTemplates: "fetchTemplates"
    }),
    ...mapActions("common", {
      trackEvent: "trackEvent"
    }),
    fetch() {
      this.fetchTemplates({
        shop: this.shopInfo.shopifyDomain,
        tags: this.type,
        onSuccess: () => {
          this.fetched = true;
        }
      });
    },
    enterName(id) {
      this.template_id = id;
      this.showModal = true;
      this.autoFocus = true;

      this.trackCreateFromTemplateClickedEvent();
    },
    previewTemplate(id) {
      this.template_id = id;
      this.showPreview = true;
    },
    closePreview() {
      this.showPreview = false;
    },
    selectTemplate(id) {
      this.template_id = id;
      this.openModal();
    },
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    openBlankModal() {
      this.openModal();
      this.template_id = "0";

      this.trackCreateBlankClickedEvent();
    },
    updateRoute(val) {
      this.$router.push({ name: val });
    },
    setTemplateType(type) {
      if (!type || !type.length) {
        return;
      }
      switch (type) {
        case "articlepages": {
          this.templateType = "ArticlePagesTemplate";
          this.templateOrder = ["articlepages", "", "landingpages", "subpages", "homepages", "productpages", "collectionpages"];
          break;
        }
        case "homepages": {
          this.templateType = "HomePagesTemplate";
          this.templateOrder = ["homepages", "", "landingpages", "subpages", "productpages", "collectionpages", "articlepages"];
          break;
        }
        case "productpages": {
          this.templateType = "ProductPagesTemplate";
          this.templateOrder = ["productpages", "", "landingpages", "subpages", "homepages", "collectionpages", "articlepages"];
          break;
        }
        case "collectionpages": {
          this.templateType = "CollectionPagesTemplate";
          this.templateOrder = ["collectionpages", "", "landingpages", "subpages", "homepages", "productpages", "articlepages"];
          break;
        }
        default: {
          this.templateType = "LandingPagesTemplate";
          this.templateOrder = ["landingpages", "subpages", "", "homepages", "productpages", "collectionpages", "articlepages"];
          break;
        }
      }
    },
    trackTemplateViewedEvent() {
      let eventId = null;

      switch (this.type) {
        case "subpages":
          eventId = LANDING_PAGE_EVENT.TEMPLATE_VIEWED;
          break;
        case "articlepages":
          eventId = BLOG_POST_EVENT.TEMPLATE_VIEWED;
          break;
        case "homepages":
          eventId = HOME_PAGE_EVENT.TEMPLATE_VIEWED;
          break;
        case "collectionpages":
          eventId = COLLECTION_PAGE_EVENT.TEMPLATE_VIEWED;
          break;
        case "productpages":
          eventId = PRODUCT_PAGE_EVENT.TEMPLATE_VIEWED;
          break;
      }

      this.trackEvent({ event_id: eventId });
    },

    trackCreateBlankClickedEvent() {
      let eventId = null;

      switch (this.type) {
        case "subpages":
          eventId = LANDING_PAGE_EVENT.CREATE_BLANK_CLICKED;
          break;
        case "articlepages":
          eventId = BLOG_POST_EVENT.CREATE_BLANK_CLICKED;
          break;
        case "homepages":
          eventId = HOME_PAGE_EVENT.CREATE_BLANK_CLICKED;
          break;
        case "collectionpages":
          eventId = COLLECTION_PAGE_EVENT.CREATE_BLANK_CLICKED;
          break;
        case "productpages":
          eventId = PRODUCT_PAGE_EVENT.CREATE_BLANK_CLICKED;
          break;
      }

      this.trackEvent({ event_id: eventId });
    },

    trackCreateFromTemplateClickedEvent() {
      let eventId = null;

      switch (this.type) {
        case "subpages":
          eventId = LANDING_PAGE_EVENT.CREATE_FROM_TEMPLATE_CLICKED;
          break;
        case "articlepages":
          eventId = BLOG_POST_EVENT.CREATE_FROM_TEMPLATE_CLICKED;
          break;
        case "homepages":
          eventId = HOME_PAGE_EVENT.CREATE_FROM_TEMPLATE_CLICKED;
          break;
        case "collectionpages":
          eventId = COLLECTION_PAGE_EVENT.CREATE_FROM_TEMPLATE_CLICKED;
          break;
        case "productpages":
          eventId = PRODUCT_PAGE_EVENT.CREATE_FROM_TEMPLATE_CLICKED;
          break;
      }

      this.trackEvent({ event_id: eventId, properties: { template_id: this.template_id } });
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (!vm.permissions[vm.type]) {
        if (from.name != "Pricing") vm.$router.push({ name: "Pricing" });
        else vm.$router.push({ name: "Dashboard" });
      }
      return true;
    });
  },
  getUserIndustryServer() {
    const surveys = this.shopInfo && this.shopInfo.surveys;
    if (!surveys) return;
    const newSurveyObs = surveys.filter(sv => sv.handle == "onboarding-v2") || [];
    if (newSurveyObs && newSurveyObs.length > 0) {
      newSurveyObs?.industries?.reverse().forEach(item =>
        this.templateOrder.unshift(
          item
            .toLowerCase()
            .replace(" & ", "-")
            .replace(", ", "-")
            .replace(" ", "-")
        )
      );
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep {
  .page-block {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    border-radius: $radius;
    flex: 1;
    // margin-left: 24px;
    margin-top: 24px;
    padding: 16px;
    // 228x318
    // min-width: calc(228px);
    // min-height: calc(318px);
    // width: calc(25% - 24px);
    // max-width: calc(228px);
    // max-height: calc(318px);
  }
}
.template-for {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  width: 100%;
}

::v-deep {
  .dropdown_toggle {
    justify-content: start;
    h1 {
      width: unset;
    }
  }
}
</style>
